import { Suspense, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Avatar, ErrorBoundary, IconBtn, Menu, MenuHead, MenuItem, MenuSep } from './ui';
import BrandLogo from './BrandLogo';
import { ProfileNudge } from './ProfileNudge';
import { Concierge } from './Concierge';
import { MethodologyModal } from './MethodologyModal';
import { NewWorkspaceModal } from './NewWorkspaceModal';
import { orgWorkspaces, workspaceQuota } from '../lib/workspaces';
import Breadcrumbs from './Breadcrumbs';
import { onboarding, relationships } from '../api/org';
import type { OnboardingPath, OutreachReminder } from '../api/types';
import FirstRunTour, { replayTour } from './FirstRunTour';
import { RouteFallback } from './feedback';
import {
  ActivityIcon,
  AnalyzeIcon,
  ApplicationsIcon,
  AwardsIcon,
  BellIcon,
  BookOpenIcon,
  BuildingIcon,
  CalendarIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DiscoverIcon,
  DocumentsIcon,
  HeartIcon,
  HelpCircleIcon,
  HomeIcon,
  LogoutIcon,
  MenuIcon,
  OverviewIcon,
  PipelineIcon,
  PlusIcon,
  PortfolioIcon,
  ProfileIcon,
  ProposalsIcon,
  ReadinessIcon,
  RegistrationsIcon,
  ReportsIcon,
  SavedIcon,
  ShieldCheckIcon,
  TasksIcon,
  TeamIcon,
  VaultIcon,
} from './icons';

interface NavItem {
  to: string;
  label: string;
  /** Flat SVG icon component — or Val's ✦ brand mark. */
  icon: ReactNode;
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/app/overview', label: 'Overview', icon: <OverviewIcon /> },
  { to: '/app/activity', label: 'Activity', icon: <ActivityIcon /> },
  { to: '/app/discover', label: 'Discover', icon: <DiscoverIcon /> },
  { to: '/app/analyze', label: 'Analyze', icon: <AnalyzeIcon /> },
  { to: '/app/saved', label: 'Saved', icon: <SavedIcon /> },
  { to: '/app/pipeline', label: 'Pipeline', icon: <PipelineIcon /> },
  { to: '/app/proposals', label: 'Proposals', icon: <ProposalsIcon /> },
  { to: '/app/awards', label: 'Awards', icon: <AwardsIcon /> },
  { to: '/app/readiness', label: 'Readiness', icon: <ReadinessIcon /> },
  { to: '/app/govcon-readiness', label: 'GovCon Readiness', icon: <ShieldCheckIcon /> },
  { to: '/app/calendar', label: 'Calendar', icon: <CalendarIcon /> },
  { to: '/app/agents', label: 'Ask Val…', icon: '✦' },
];

const SECONDARY_NAV: NavItem[] = [
  { to: '/app/team', label: 'Stakeholders', icon: <TeamIcon /> },
  { to: '/app/relationships', label: 'Relationships', icon: <HeartIcon /> },
  { to: '/app/organization', label: 'Organization', icon: <BuildingIcon /> },
  { to: '/app/vault', label: 'Vault', icon: <VaultIcon /> },
  { to: '/app/registrations', label: 'Registrations', icon: <RegistrationsIcon /> },
  { to: '/app/reports', label: 'Reports', icon: <ReportsIcon /> },
  { to: '/app/tasks', label: 'Tasks', icon: <TasksIcon /> },
  { to: '/app/documents', label: 'Documents', icon: <DocumentsIcon /> },
];

/** Navigation for personal (Individual) workspaces — no org/GovCon features. */
const INDIVIDUAL_NAV: NavItem[] = [
  { to: '/app/individual', label: 'Home', icon: <HomeIcon /> },
  { to: '/app/individual/discover', label: 'Discover', icon: <DiscoverIcon /> },
  { to: '/app/individual/applications', label: 'My Applications', icon: <ApplicationsIcon /> },
  { to: '/app/individual/profile', label: 'My Profile', icon: <ProfileIcon /> },
];

const INDIVIDUAL_MOBILE_NAV: NavItem[] = [
  { to: '/app/individual', label: 'Home', icon: <HomeIcon /> },
  { to: '/app/individual/discover', label: 'Discover', icon: <DiscoverIcon /> },
  { to: '/app/individual/applications', label: 'Applications', icon: <ApplicationsIcon /> },
  { to: '/app/individual/profile', label: 'Profile', icon: <ProfileIcon /> },
];

function RailLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <NavLink
      to={item.to}
      data-tour={item.to}
      className={({ isActive }) => `rail-link${isActive ? ' active' : ''}`}
      title={collapsed ? item.label : undefined}
    >
      <span className="rail-icon" aria-hidden="true">{item.icon}</span>
      <span>{item.label}</span>
    </NavLink>
  );
}

/** Universal search: jumps to Discover with the query. */
function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isIndividual } = useAuth();
  const [q, setQ] = useState('');
  const discoverPath = isIndividual ? '/app/individual/discover' : '/app/discover';
  if (location.pathname.startsWith(discoverPath)) return null;
  return (
    <form
      className="topbar-search"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        navigate(`${discoverPath}?q=${encodeURIComponent(q)}`);
      }}
    >
      <input
        className="input"
        type="search"
        placeholder={isIndividual ? 'Search scholarships, grants, keywords…  ( / )' : 'Search opportunities, agencies, keywords…  ( / )'}
        aria-label={isIndividual ? 'Search scholarships and grants' : 'Search opportunities'}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ borderRadius: 'var(--r-full)' }}
      />
    </form>
  );
}

export function AppLayout() {
  const { user, memberships, currentOrg, selectOrg, logout, isIndividual } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const railRef = useRef<HTMLElement>(null);

  // Close the mobile drawer on navigation.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  // Mobile drawer behaves as a dialog: Esc closes, focus moves in on open
  // and returns to the menu button on close.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      railRef.current?.querySelector<HTMLElement>('a.rail-link')?.focus();
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      menuBtnRef.current?.focus();
    };
  }, [mobileOpen]);

  // Lock body scroll when the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Keyboard shortcut: "/" focuses global search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !location.pathname.startsWith('/app/discover')) {
        const t = e.target as HTMLElement;
        if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
        e.preventDefault();
        document.querySelector<HTMLInputElement>('.topbar-search input')?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [location.pathname]);

  const railClass = `rail${collapsed ? ' collapsed' : ''}${mobileOpen ? ' open' : ''}`;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const [dueReminders, setDueReminders] = useState<OutreachReminder[]>([]);
  const currentOrgId = currentOrg?.organizationId;

  // R2-BR-D69: reminders are workspace-scoped. Clear and refetch whenever
  // the workspace changes so the previous workspace's follow-ups never
  // paint under the new workspace's header.
  useEffect(() => {
    setDueReminders([]);
    if (isIndividual || !currentOrgId) return;
    let cancelled = false;
    relationships
      .reminders.due()
      .then((res) => { if (!cancelled) setDueReminders(res.items); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isIndividual, currentOrgId]);

  // The current workspace's onboarding path ('NONPROFIT' | 'SMALLBIZ') drives
  // rail ordering below. Null = not onboarded yet / unknown → default order.
  const [onboardingPath, setOnboardingPath] = useState<OnboardingPath | null>(null);

  useEffect(() => {
    setOnboardingPath(null);
    if (isIndividual || !currentOrgId) return;
    let cancelled = false;
    onboarding
      .get()
      .then((res) => { if (!cancelled) setOnboardingPath(res.profile?.path ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isIndividual, currentOrgId]);

  // Path-aware rail: nonprofits live in grant Readiness, so GovCon Readiness
  // drops to the Manage group; small businesses live in GovCon, so grant
  // Readiness drops instead. Every destination stays one click away in the
  // other group — nothing is hidden.
  let primaryNav = PRIMARY_NAV;
  let secondaryNav = SECONDARY_NAV;
  if (onboardingPath === 'NONPROFIT') {
    const govcon = PRIMARY_NAV.find((i) => i.to === '/app/govcon-readiness');
    primaryNav = PRIMARY_NAV.filter((i) => i.to !== '/app/govcon-readiness');
    if (govcon) secondaryNav = [govcon, ...SECONDARY_NAV];
  } else if (onboardingPath === 'SMALLBIZ') {
    const readiness = PRIMARY_NAV.find((i) => i.to === '/app/readiness');
    primaryNav = PRIMARY_NAV.filter((i) => i.to !== '/app/readiness');
    if (readiness) secondaryNav = [readiness, ...SECONDARY_NAV];
  }

  const reminderTone = (dueAt: string): 'warning' | 'info' => {
    return new Date(dueAt).getTime() < Date.now() ? 'warning' : 'info';
  };

  const notifications = dueReminders.slice(0, 5).map((r) => ({
    id: `reminder-${r.id}`,
    title: new Date(r.dueAt).getTime() < Date.now() ? 'Overdue follow-up' : 'Follow-up due soon',
    body: `${r.title}${r.stakeholder ? ` — ${r.stakeholder.name}` : ''}`,
    tone: reminderTone(r.dueAt),
    to: '/app/relationships' as const,
  }));

  return (
    <div className="app-shell">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      {mobileOpen && <div className="rail-scrim" onClick={() => setMobileOpen(false)} aria-hidden="true" />}

      <aside
        ref={railRef}
        className={railClass}
        role={mobileOpen ? 'dialog' : undefined}
        aria-modal={mobileOpen || undefined}
        aria-label={mobileOpen ? 'Navigation' : undefined}
      >
        <div className="rail-brand">
          <BrandLogo variant="white" height={44} />
        </div>

        <nav className="rail-nav" aria-label="Primary">
          {isIndividual ? (
            <>
              <div className="rail-section-label">Personal</div>
              {INDIVIDUAL_NAV.map((item) => (
                <RailLink key={item.to} item={item} collapsed={collapsed} />
              ))}
            </>
          ) : (
            <>
              <div className="rail-section-label">Workspace</div>
              {primaryNav.map((item) => (
                <RailLink key={item.to} item={item} collapsed={collapsed} />
              ))}
              {orgWorkspaces(memberships).length > 1 && (
                <RailLink
                  key="/app/portfolio"
                  item={{ to: '/app/portfolio', label: 'Portfolio', icon: <PortfolioIcon /> }}
                  collapsed={collapsed}
                />
              )}
              <div className="rail-section-label">Manage</div>
              {secondaryNav.map((item) => (
                <RailLink key={item.to} item={item} collapsed={collapsed} />
              ))}
            </>
          )}
        </nav>

        <div className="rail-footer">
          <button
            type="button"
            className="rail-collapse-btn"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {collapsed ? <ChevronRightIcon size={16} /> : <ChevronLeftIcon size={16} />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <IconBtn
            label="Open navigation"
            onClick={() => setMobileOpen(true)}
            className="mobile-menu-btn"
            ref={menuBtnRef}
          >
            <span aria-hidden="true"><MenuIcon size={20} /></span>
          </IconBtn>

          <GlobalSearch />

          <div className="topbar-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => navigate('/app/how-to')}
            >
              <span aria-hidden="true" style={{ display: 'inline-flex' }}><BookOpenIcon size={14} /></span> How-To
            </button>
            {!isIndividual && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/app/opportunities/new')}
              >
                <span aria-hidden="true" style={{ display: 'inline-flex' }}><PlusIcon size={14} /></span> Add opportunity
              </button>
            )}

            <Menu
              label={`Notifications${notifications.length ? `, ${notifications.length} unread` : ''}`}
              trigger={
                <IconBtn label={`Notifications${notifications.length ? `, ${notifications.length} unread` : ''}`}>
                  <span aria-hidden="true"><BellIcon size={20} /></span>
                  {notifications.length > 0 && <span className="dot-badge" aria-hidden="true" />}
                </IconBtn>
              }
            >
              <MenuHead>Notifications</MenuHead>
              {notifications.length === 0 && (
                <div className="menu-head" style={{ textTransform: 'none', letterSpacing: 0 }}>
                  You&apos;re all caught up
                </div>
              )}
              {notifications.map((n) => (
                <MenuItem key={n.id} onClick={() => navigate(n.to)}>
                  <span>
                    <strong>{n.title}</strong>
                    <br />
                    <span className="text-sm text-muted">{n.body}</span>
                  </span>
                </MenuItem>
              ))}
              <MenuSep />
              <MenuItem onClick={() => navigate('/app/relationships')}>Relationship follow-ups →</MenuItem>
            </Menu>

            <Menu
              label="Help and resources"
              trigger={<IconBtn label="Help and resources"><span aria-hidden="true"><HelpCircleIcon size={20} /></span></IconBtn>}
            >
              <MenuHead>Help</MenuHead>
              <MenuItem onClick={() => navigate('/app/agents/harbor')}>✦ Ask Harbor — onboarding guide</MenuItem>
              <MenuItem onClick={() => navigate('/app/agents')}>✦ Meet Val</MenuItem>
              <MenuSep />
              <MenuItem onClick={() => setMethodologyOpen(true)}>Methodology &amp; scoring</MenuItem>
            </Menu>

            {!isIndividual && (
              <Menu
                label="Workspaces"
                trigger={
                  <button type="button" className="btn btn-ghost btn-sm" aria-label="Workspaces">
                    <span aria-hidden="true" style={{ display: 'inline-flex' }}><BuildingIcon size={16} /></span>
                    <span className="text-sm">{currentOrg?.organizationName}</span>
                  </button>
                }
              >
                <MenuHead>Workspaces</MenuHead>
                {orgWorkspaces(memberships).map((m) => (
                  <MenuItem
                    key={m.organizationId}
                    onClick={() => selectOrg(m.organizationId)}
                  >
                    {m.organizationName}
                    {m.organizationId === currentOrg?.organizationId && (
                      <span aria-hidden="true" style={{ display: 'inline-flex', marginLeft: 6, verticalAlign: '-2px' }}><CheckIcon size={14} /></span>
                    )}
                  </MenuItem>
                ))}
                <MenuSep />
                {(() => {
                  const quota = workspaceQuota(memberships);
                  const isOwner = currentOrg?.role === 'OWNER';
                  if (isOwner && quota.canCreate) {
                    return (
                      <MenuItem onClick={() => setWorkspaceModalOpen(true)}>
                        <span aria-hidden="true" style={{ display: 'inline-flex', marginRight: 6, verticalAlign: '-2px' }}><PlusIcon size={14} /></span>
                        New workspace ({quota.used} of {quota.limit} used)
                      </MenuItem>
                    );
                  }
                  if (isOwner && !quota.isEnterprise) {
                    return (
                      <MenuItem onClick={() => navigate('/app/billing')}>
                        Unlock a 2nd workspace — Enterprise
                      </MenuItem>
                    );
                  }
                  return null;
                })()}
              </Menu>
            )}

            <Menu
              label="Account"
              trigger={
                <button type="button" className="user-chip-btn" aria-label="Account menu">
                  <Avatar name={user?.name ?? '?'} />
                </button>
              }
            >
              <MenuHead>{user?.name ?? 'Account'}</MenuHead>
              <div className="menu-head" style={{ textTransform: 'none', letterSpacing: 0 }}>{user?.email}</div>
              <MenuSep />
              {isIndividual ? (
                <>
                  <MenuItem onClick={() => navigate('/app/individual/profile')}>My profile</MenuItem>
                  <MenuItem onClick={() => navigate('/app/individual/profile#plan-billing')}>Billing</MenuItem>
                </>
              ) : (
                <>
                  <MenuItem onClick={() => navigate('/app/organization')}>Organization profile</MenuItem>
                  <MenuItem onClick={() => navigate('/app/billing')}>Billing</MenuItem>
                  <MenuItem onClick={() => navigate('/app/settings')}>Settings</MenuItem>
                </>
              )}
              <MenuSep />
              <MenuItem onClick={replayTour}>Take the tour</MenuItem>
              <MenuSep />
              <MenuItem danger onClick={handleLogout}>
                <span aria-hidden="true" style={{ display: 'inline-flex', marginRight: 6, verticalAlign: '-2px' }}><LogoutIcon size={14} /></span>
                Log out
              </MenuItem>
            </Menu>
          </div>
        </header>

        <Breadcrumbs />

        <main className="app-content" id="main-content">
          <ErrorBoundary
            title="This page ran into a problem."
            resetLabel="Reload the page"
            onReset={() => window.location.reload()}
          >
            <Suspense fallback={<RouteFallback />}>
              {/* R2-BR-D69: pages fetch on mount and their effects do not
                  track the workspace, so staying on the same route across
                  a workspace switch left the previous workspace's data
                  rendered under the new header until a reload. Keying the
                  routed content by workspace id remounts the page on
                  switch; the session cookie has already been switched
                  server-side by selectOrg before this state lands, so the
                  remount refetches under the new workspace only. */}
              <Outlet key={currentOrgId ?? 'no-workspace'} />
            </Suspense>
          </ErrorBoundary>
        </main>

        <nav className="mobile-nav" aria-label="Primary navigation">
          {(isIndividual ? INDIVIDUAL_MOBILE_NAV : [
            { to: '/app/overview', label: 'Overview', icon: <OverviewIcon /> },
            { to: '/app/discover', label: 'Discover', icon: <DiscoverIcon /> },
            { to: '/app/pipeline', label: 'Pipeline', icon: <PipelineIcon /> },
            ...(orgWorkspaces(memberships).length > 1
              ? [{ to: '/app/portfolio', label: 'Portfolio', icon: <PortfolioIcon /> }]
              : []),
            { to: '/app/proposals', label: 'Proposals', icon: <ProposalsIcon /> },
            { to: '/app/agents', label: 'Ask Val…', icon: '✦' },
          ]).map((item) => (
            <NavLink key={item.to} to={item.to} data-tour={item.to} className={({ isActive }) => (isActive ? 'active' : '')}>
              <span aria-hidden="true">{item.icon}</span>{item.label}
            </NavLink>
          ))}
        </nav>
        {/* R2-BR-D69: both hold workspace-scoped state (profile
            completeness; Val conversation) — remount per workspace. */}
        <ProfileNudge key={`nudge-${currentOrgId ?? 'none'}`} />
        <Concierge key={`concierge-${currentOrgId ?? 'none'}`} />
        <FirstRunTour isIndividual={isIndividual} userId={user?.id} />
        <MethodologyModal open={methodologyOpen} onClose={() => setMethodologyOpen(false)} />
        {workspaceModalOpen && <NewWorkspaceModal onClose={() => setWorkspaceModalOpen(false)} />}
      </div>
    </div>
  );
}
